import { useState, useCallback } from 'react';

export interface ModuleConfig {
  id: string;
  title: string;
  collapsed: boolean;
  order: number;
  span?: 'full' | 'half';
}

function loadLayout(key: string, defaults: ModuleConfig[]): ModuleConfig[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return defaults;
    const saved: ModuleConfig[] = JSON.parse(raw);
    // Merge saved order/collapsed state with defaults (in case new modules were added)
    const savedMap = new Map(saved.map((m) => [m.id, m]));
    return defaults
      .map((d) => {
        const s = savedMap.get(d.id);
        return s ? { ...d, order: s.order, collapsed: s.collapsed } : d;
      })
      .sort((a, b) => a.order - b.order);
  } catch {
    return defaults;
  }
}

function persistLayout(key: string, modules: ModuleConfig[]) {
  try {
    localStorage.setItem(key, JSON.stringify(modules));
  } catch {
    // Storage full — silently ignore
  }
}

export function useModuleLayout(dashboardId: string, defaultModules: ModuleConfig[]) {
  const storageKey = `eg-dashboard-${dashboardId}`;
  const [modules, setModules] = useState<ModuleConfig[]>(() => loadLayout(storageKey, defaultModules));

  const moveModule = useCallback(
    (activeId: string, overId: string) => {
      setModules((prev) => {
        const oldIndex = prev.findIndex((m) => m.id === activeId);
        const newIndex = prev.findIndex((m) => m.id === overId);
        if (oldIndex === -1 || newIndex === -1) return prev;

        const next = [...prev];
        const [removed] = next.splice(oldIndex, 1);
        next.splice(newIndex, 0, removed);
        const reordered = next.map((m, i) => ({ ...m, order: i }));
        persistLayout(storageKey, reordered);
        return reordered;
      });
    },
    [storageKey],
  );

  const toggleCollapse = useCallback(
    (moduleId: string) => {
      setModules((prev) => {
        const next = prev.map((m) => (m.id === moduleId ? { ...m, collapsed: !m.collapsed } : m));
        persistLayout(storageKey, next);
        return next;
      });
    },
    [storageKey],
  );

  const resetLayout = useCallback(() => {
    setModules(defaultModules);
    persistLayout(storageKey, defaultModules);
  }, [storageKey, defaultModules]);

  return { modules, moveModule, toggleCollapse, resetLayout };
}
